import * as angular from 'angular';
import * as _ from 'lodash';

import * as Actions from './variables.constants';

import { VariableType, VariableSubType, Qualifier, IVariableTypeFileModel } from '../../models';
import { IVariableService } from '../../services';
import { Dispatcher, GetStore } from '../Store';

export interface IVariablesActionsService {
    getVariableTypes(): Dispatcher<VariableType[]>;
    getVariableTypes(force: boolean): Dispatcher<VariableType[]>;
    getVariableSubTypes(): Dispatcher<VariableSubType[]>;
    getVariableSubTypes(force: boolean): Dispatcher<VariableSubType[]>;
    getQualifiers(): Dispatcher<Qualifier[]>;
    getQualifiers(force: boolean): Dispatcher<Qualifier[]>;
    updateAll(value: IVariableTypeFileModel);
}

export class VariablesActionsService implements IVariablesActionsService {
    static serviceId = 'variablesActionsService';
    static $inject = ['$q', 'variableService'];

    constructor(
        private _$q: angular.IQService,
        private _variableService: IVariableService,
    ) { }

    getVariableTypes(): Dispatcher<VariableType[]>;
    getVariableTypes(force: boolean = false): Dispatcher<VariableType[]> {
        return (dispatch: Function, getState: GetStore) => {
            const types = getState().variablesTypes;
            if (force || !types || (!types.isLoading && !types.isLoaded)) {
                dispatch({ type: Actions.VARIABLES_LOADING, payload: null });
                return this._variableService
                    .getAll(force)
                    .then(types => {
                        const results = types.map(t => t.variable);
                        dispatch({ type: Actions.VARIABLES_LOAD_VARIABLETYPE, payload: results });

                        // also load units
                        const units = _.flatten(_.values<VariableType>(results).map(v => v.unitCollection));
                        dispatch({ type: Actions.VARIABLES_LOAD_UNIT, payload: units });

                        return results;
                    })
                    .catch(reason => {
                        dispatch({ type: Actions.VARIABLES_LOAD_ERROR, payload: reason });
                        return reason;
                    });
            }
            else {
                return this._$q.when(_.values<VariableType>(types.dictionary));
            }
        };
    }

    getVariableSubTypes(): Dispatcher<VariableSubType[]>;
    getVariableSubTypes(force: boolean = false): Dispatcher<VariableSubType[]> {
        return (dispatch: Function, getState: GetStore) => {
            const subTypes = getState().variableSubTypes;
            if (force || !subTypes || (!subTypes.isLoading && !subTypes.isLoaded)) {
                dispatch({ type: Actions.VARIABLES_LOADING, payload: null });
                return this._variableService
                    .getAllSubtypes(force)
                    .then(subTypes => {
                        dispatch({ type: Actions.VARIABLES_LOAD_VARIABLESUBTYPE, payload: subTypes });
                        return subTypes;
                    })
                    .catch(reason => {
                        dispatch({ type: Actions.VARIABLES_LOAD_ERROR, payload: reason });
                        return reason;
                    });
            }
            else {
                return this._$q.when(_.values<VariableSubType>(subTypes.dictionary));
            }
        };
    }

    getQualifiers(): Dispatcher<Qualifier[]>;
    getQualifiers(force: boolean = false): Dispatcher<Qualifier[]> {
        return (dispatch: Function, getState: GetStore) => {
            const qualifiers = getState().qualifiers;
            if (force || !qualifiers || (!qualifiers.isLoading && !qualifiers.isLoaded)) {
                dispatch({ type: Actions.VARIABLES_LOADING, payload: null });
                return this._variableService
                    .getAllQualifiers(force)
                    .then(qualifiers => {
                        dispatch({ type: Actions.VARIABLES_LOAD_QUALIFIER, payload: qualifiers });
                        return qualifiers;
                    })
                    .catch(reason => {
                        dispatch({ type: Actions.VARIABLES_LOAD_ERROR, payload: reason });
                        return reason;
                    });
            }
            else {
                return this._$q.when(_.values<Qualifier>(qualifiers.dictionary));
            }
        };
    }

    updateAll(value: IVariableTypeFileModel) {
        return (dispatch: Function, getState: GetStore) => {
            dispatch({ type: Actions.VARIABLES_LOAD_VARIABLETYPE, payload: value.variableTypes });
            dispatch({ type: Actions.VARIABLES_LOAD_VARIABLESUBTYPE, payload: value.variableSubTypes });
            dispatch({ type: Actions.VARIABLES_LOAD_QUALIFIER, payload: value.qualifiers });
            return;
        };
    }
}